import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useConfig } from "@/hooks/useConfig";
import { apiRequest, apiUploadFiles, queryClient } from "@/lib/queryClient";
import { Building2, AlertCircle, Loader2, Upload, FileText, Trash2, CreditCard } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useEffect, useState, useCallback, useRef } from "react";
import type { CompanyProfile, User } from "@shared/schema";
import BrandKitCard from "@/components/brand/BrandKitCard";
import { subscriptionSummary, type SubscriptionFields } from "@/lib/paymentGrace";
import { useHotelModule, useIsHotelEdition } from "@/lib/hotelEdition";
import { Link } from "wouter";

const companyProfileSchema = z.object({
  name: z.string().min(1, "Firmenname ist erforderlich"),
  description: z.string().optional(),
  industry: z.string().min(1, "Branche ist erforderlich"),
  // Optional for everyone (PLAN §1 item 2, Mike A5): "" = no LinkedIn page.
  linkedinProfile: z.string().url("Bitte geben Sie eine gültige LinkedIn-Adresse ein").or(z.literal("")),
  // Hotels only; the server normalizes it like the check-in (@name or instagram.com/name).
  instagramHandle: z.string().optional(),
  contactFirstName: z.string().min(1, "Vorname ist erforderlich"),
  contactLastName: z.string().min(1, "Nachname ist erforderlich"),
  contactEmail: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
  contactPhone: z.string().min(1, "Telefonnummer ist erforderlich"),
  website: z.string().url("Bitte geben Sie eine gültige Website-Adresse ein"),
  address: z.string().min(1, "Adresse ist erforderlich"),
  logoUrl: z.string().url().optional().or(z.literal("")),
});

type CompanyProfileForm = z.infer<typeof companyProfileSchema>;

interface CompanyFile {
  fileName: string;
  filePath: string;
  fileSize: number;
  lastModified: string;
}

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function Profil() {
  const { toast } = useToast();
  const { user } = useAuth();
  const account = user as User | undefined;
  const { paymentRequired } = useConfig();
  // /api/auth/me also carries paymentIssue/graceUntil (not on the User type).
  const subscription = subscriptionSummary(user as SubscriptionFields | undefined);

  const billingPortalMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/billing/portal", {}),
    onSuccess: (res: { url?: string }) => {
      if (res?.url) window.location.href = res.url;
      else toast({ title: "Fehler", description: "Portal-Link fehlt.", variant: "destructive" });
    },
    onError: (err: any) => {
      toast({ title: "Fehler", description: err?.data?.error || "Billing-Portal nicht verfügbar.", variant: "destructive" });
    },
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { data: company, isLoading } = useQuery<CompanyProfile>({
    queryKey: ['/api/companies/me']
  });
  const isHotel = useIsHotelEdition();
  // With the 'marke' module live the brand kit has its own page (/marke) and leaves the profile.
  const brandOnOwnPage = useHotelModule("marke");

  const { data: filesData, isLoading: filesLoading, refetch: refetchFiles } = useQuery<{ files: CompanyFile[] }>({
    queryKey: ['/api/companies/files'],
    enabled: !!company,
  });

  const profileForm = useForm<CompanyProfileForm>({
    resolver: zodResolver(companyProfileSchema),
    defaultValues: {
      name: "",
      description: "",
      industry: "",
      linkedinProfile: "",
      instagramHandle: "",
      contactFirstName: "",
      contactLastName: "",
      contactEmail: "",
      contactPhone: "",
      website: "",
      address: "",
      logoUrl: "",
    },
  });

  // keepDirtyValues: a background refresh of the company (hotel capability polling) keeps unsaved edits.
  const formValuesFrom = (c: CompanyProfile): CompanyProfileForm => ({
    name: c.name || "",
    description: c.description || "",
    industry: c.industry || "",
    linkedinProfile: c.linkedinProfile || "",
    instagramHandle: c.instagramHandle || "",
    contactFirstName: c.contactFirstName || "",
    contactLastName: c.contactLastName || "",
    contactEmail: c.contactEmail || "",
    contactPhone: c.contactPhone || "",
    website: c.website || "",
    address: c.address || "",
    logoUrl: c.logoUrl || "",
  });

  useEffect(() => {
    if (company) {
      profileForm.reset(formValuesFrom(company), { keepDirtyValues: true });
    }
  }, [company, profileForm]);

  const isDirty = profileForm.formState.isDirty;

  const updateProfileMutation = useMutation({
    mutationFn: async ({ instagramHandle, ...data }: CompanyProfileForm) => {
      // apiRequest already returns parsed JSON and throws on error. Only hotels edit the Instagram field.
      return await apiRequest("PATCH", "/api/companies/me", isHotel ? { ...data, instagramHandle: instagramHandle ?? "" } : data);
    },
    onSuccess: (updated: CompanyProfile | undefined, submitted: CompanyProfileForm) => {
      // Adopt the saved (server-normalized) values and clear the dirty state; keepDirtyValues would keep it.
      // A field edited while the request ran keeps what was typed and stays dirty (review he-a R2-3).
      if (updated) {
        const current = profileForm.getValues();
        profileForm.reset(formValuesFrom(updated));
        for (const key of Object.keys(current) as (keyof CompanyProfileForm)[]) {
          if (current[key] !== submitted[key]) profileForm.setValue(key, current[key], { shouldDirty: true });
        }
      }
      queryClient.invalidateQueries({ queryKey: ['/api/companies/me'] });
      toast({
        title: "Profil gespeichert",
        description: "Ihr Firmenprofil wurde aktualisiert.",
      });
    },
    onError: (error: any) => {
      console.error("Profile save error:", error);
      // apiRequest attaches error data in error.data
      const message = error?.data?.error || error?.data?.message || error?.message || "Ein Fehler ist aufgetreten";
      toast({
        title: "Fehler",
        description: message,
        variant: "destructive",
      });
    },
  });

  const onSubmitProfile = (data: CompanyProfileForm) => {
    updateProfileMutation.mutate(data);
  };

  const deleteFileMutation = useMutation({
    mutationFn: async (fileName: string) => {
      return await apiRequest("DELETE", `/api/companies/files/${encodeURIComponent(fileName)}`);
    },
    onSuccess: () => {
      refetchFiles();
      toast({
        title: "Datei gelöscht",
        description: "Die Datei wurde erfolgreich entfernt.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error?.data?.message || "Datei konnte nicht gelöscht werden",
        variant: "destructive",
      });
    },
  });

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const validateFiles = (files: File[]): File[] => {
    const validFiles: File[] = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "Datei zu groß",
          description: `${file.name} ist größer als 10MB`,
          variant: "destructive",
        });
        continue;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        toast({
          title: "Ungültiger Dateityp",
          description: `${file.name} hat einen nicht unterstützten Dateityp`,
          variant: "destructive",
        });
        continue;
      }
      validFiles.push(file);
    }
    return validFiles;
  };

  const uploadFilesMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      return await apiUploadFiles('/api/companies/files', formData);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['/api/companies/files'] });

      if (result.failedFiles && result.failedFiles.length > 0) {
        toast({
          title: "Teilweise hochgeladen",
          description: `${result.files.length} Datei(en) erfolgreich, ${result.failedFiles.length} fehlgeschlagen`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Hochgeladen",
          description: result.message,
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Upload fehlgeschlagen",
        description: error.message || "Ein Fehler ist aufgetreten",
        variant: "destructive",
      });
    },
  });

  const uploadFiles = (files: File[]) => {
    if (files.length === 0) return;

    const validFiles = validateFiles(files);
    if (validFiles.length === 0) return;

    uploadFilesMutation.mutate(validFiles);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    uploadFiles(files);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      uploadFiles(files);
      e.target.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center space-x-2">
        <Building2 className="h-6 w-6" />
        <h1 className="text-3xl font-bold font-playfair">Firmenprofil</h1>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Building2 className="h-5 w-5" />
            <CardTitle>Unternehmensdaten</CardTitle>
          </div>
          <CardDescription>
            Verwalten Sie die Stammdaten Ihres Hotels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={profileForm.handleSubmit(onSubmitProfile)} className="space-y-6">
            {isDirty && (
              <Alert data-testid="alert-unsaved-changes" className="border-orange-300 bg-orange-50/50 dark:bg-orange-950/20">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800 dark:text-orange-200">
                  Sie haben ungespeicherte Änderungen. Klicken Sie auf "Profil speichern", um sie zu sichern.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" data-testid="label-company-name">
                  Firmenname <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  data-testid="input-company-name"
                  {...profileForm.register("name")}
                  placeholder="Name Ihres Hotels"
                />
                {profileForm.formState.errors.name && (
                  <p className="text-sm text-red-500" data-testid="error-company-name">
                    {profileForm.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry" data-testid="label-industry">
                  Branche <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="industry"
                  data-testid="input-industry"
                  {...profileForm.register("industry")}
                  placeholder={isHotel ? "z.B. Stadthotel, Boutique-Hotel, Tagungshotel" : "z.B. IT-Consulting, Marketing"}
                />
                {profileForm.formState.errors.industry && (
                  <p className="text-sm text-red-500" data-testid="error-industry">
                    {profileForm.formState.errors.industry.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" data-testid="label-description">
                Beschreibung (optional)
              </Label>
              <Textarea
                id="description"
                data-testid="input-description"
                {...profileForm.register("description")}
                placeholder="Kurze Beschreibung Ihres Hotels"
                rows={3}
              />
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">Ansprechpartner</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contactFirstName" data-testid="label-contact-first-name">
                    Vorname <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contactFirstName"
                    data-testid="input-contact-first-name"
                    {...profileForm.register("contactFirstName")}
                    placeholder="Vorname"
                  />
                  {profileForm.formState.errors.contactFirstName && (
                    <p className="text-sm text-red-500" data-testid="error-contact-first-name">
                      {profileForm.formState.errors.contactFirstName.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactLastName" data-testid="label-contact-last-name">
                    Nachname <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contactLastName"
                    data-testid="input-contact-last-name"
                    {...profileForm.register("contactLastName")}
                    placeholder="Nachname"
                  />
                  {profileForm.formState.errors.contactLastName && (
                    <p className="text-sm text-red-500" data-testid="error-contact-last-name">
                      {profileForm.formState.errors.contactLastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="contactEmail" data-testid="label-contact-email">
                    E-Mail <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contactEmail"
                    data-testid="input-contact-email"
                    {...profileForm.register("contactEmail")}
                    placeholder="kontakt@firma.de"
                    type="email"
                  />
                  {profileForm.formState.errors.contactEmail && (
                    <p className="text-sm text-red-500" data-testid="error-contact-email">
                      {profileForm.formState.errors.contactEmail.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPhone" data-testid="label-contact-phone">
                    Telefon <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contactPhone"
                    data-testid="input-contact-phone"
                    {...profileForm.register("contactPhone")}
                    placeholder="+49 123 456789"
                    type="tel"
                  />
                  {profileForm.formState.errors.contactPhone && (
                    <p className="text-sm text-red-500" data-testid="error-contact-phone">
                      {profileForm.formState.errors.contactPhone.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">Unternehmensdetails</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="website" data-testid="label-website">
                    Website <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="website"
                    data-testid="input-website"
                    {...profileForm.register("website")}
                    placeholder="https://www.firma.de"
                    type="url"
                  />
                  {profileForm.formState.errors.website && (
                    <p className="text-sm text-red-500" data-testid="error-website">
                      {profileForm.formState.errors.website.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="linkedinProfile" data-testid="label-linkedin-profile">
                    LinkedIn-Profil (optional)
                  </Label>
                  <Input
                    id="linkedinProfile"
                    data-testid="input-linkedin-profile"
                    {...profileForm.register("linkedinProfile")}
                    placeholder="https://linkedin.com/company/firma"
                    type="url"
                  />
                  {profileForm.formState.errors.linkedinProfile && (
                    <p className="text-sm text-red-500" data-testid="error-linkedin-profile">
                      {profileForm.formState.errors.linkedinProfile.message}
                    </p>
                  )}
                </div>

                {isHotel && (
                  <div className="space-y-2">
                    <Label htmlFor="instagramHandle" data-testid="label-instagram-handle">
                      Instagram (optional)
                    </Label>
                    <Input
                      id="instagramHandle"
                      data-testid="input-instagram-handle"
                      {...profileForm.register("instagramHandle")}
                      placeholder="@ihrhotel"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2 mt-4">
                <Label htmlFor="address" data-testid="label-address">
                  Adresse <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="address"
                  data-testid="input-address"
                  {...profileForm.register("address")}
                  placeholder="Musterstraße 123, 12345 Stadt"
                  rows={2}
                />
                {profileForm.formState.errors.address && (
                  <p className="text-sm text-red-500" data-testid="error-address">
                    {profileForm.formState.errors.address.message}
                  </p>
                )}
              </div>

              <div className="space-y-2 mt-4">
                <Label htmlFor="logoUrl" data-testid="label-logo-url">
                  Logo-URL (optional)
                </Label>
                <Input
                  id="logoUrl"
                  data-testid="input-logo-url"
                  {...profileForm.register("logoUrl")}
                  placeholder="https://www.firma.de/logo.png"
                  type="url"
                />
                {profileForm.formState.errors.logoUrl && (
                  <p className="text-sm text-red-500" data-testid="error-logo-url">
                    {profileForm.formState.errors.logoUrl.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground" data-testid="hint-logo-url-brand">
                  Das Logo für Posts wird unter Marke gepflegt.
                </p>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">Firmendokumente</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Laden Sie hier wichtige Dokumente hoch (PDFs, Bilder, Office-Dokumente). Höchstens 10 MB pro Datei.
              </p>

              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                  isDragging 
                    ? 'border-primary bg-primary/5' 
                    : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="dropzone-company-files"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                  data-testid="input-company-files"
                />
                {uploadFilesMutation.isPending ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Wird hochgeladen...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Dateien hierher ziehen oder klicken zum Auswählen
                    </p>
                  </div>
                )}
              </div>

              {filesLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : filesData?.files && filesData.files.length > 0 ? (
                <div className="mt-4 space-y-2">
                  <Label className="text-sm font-medium">Hochgeladene Dateien</Label>
                  <div className="space-y-2">
                    {filesData.files.map((file, index) => (
                      <div
                        key={file.filePath}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        data-testid={`file-item-${index}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate max-w-[250px]">{file.fileName}</p>
                            <p className="text-xs text-muted-foreground">{formatFileSize(file.fileSize)}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            deleteFileMutation.mutate(file.fileName);
                          }}
                          disabled={deleteFileMutation.isPending}
                          data-testid={`button-delete-file-${index}`}
                        >
                          {deleteFileMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="submit"
                data-testid="button-save-profile"
                disabled={updateProfileMutation.isPending || !isDirty}
                className="min-w-32"
              >
                {updateProfileMutation.isPending ? "Speichert..." : "Profil speichern"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {brandOnOwnPage ? (
        <Card data-testid="card-brand-moved">
          <CardHeader>
            <CardTitle>Marke</CardTitle>
            <CardDescription>
              Logo, Farben, Schriften und Stil Ihres Hauses pflegen Sie jetzt unter{" "}
              <Link href="/marke" className="text-primary underline">Marke</Link>.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <BrandKitCard />
      )}

      {/* Subscription & billing (Phase 4). Shown when billing is live; the Stripe Customer Portal
          handles card update / cancellation / receipts — no card data ever touches our servers. */}
      {paymentRequired && (
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5" />
              <CardTitle>Abonnement & Abrechnung</CardTitle>
            </div>
            <CardDescription>
              Verwalten Sie Ihr Abonnement, Ihre Zahlungsmethode und Ihre Rechnungen.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p
                  className={`text-sm font-medium ${subscription.paymentFailed ? "text-red-700" : "text-foreground"}`}
                  data-testid="text-subscription-status"
                >
                  {subscription.label}
                </p>
                {subscription.detail && (
                  <p className="text-sm text-muted-foreground" data-testid="text-subscription-detail">
                    {subscription.detail}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => billingPortalMutation.mutate()}
              disabled={billingPortalMutation.isPending}
              data-testid="button-billing-portal"
            >
              {billingPortalMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : subscription.paymentFailed ? (
                "Zahlungsmethode aktualisieren"
              ) : (
                "Abrechnung verwalten"
              )}
            </Button>
            {subscription.renewalNote && (
              <p className="text-xs text-muted-foreground" data-testid="text-subscription-renewal">
                Ihr Abonnement verlängert sich automatisch um jeweils einen Monat. Sie können es jederzeit zum Ende
                des laufenden Abrechnungsmonats im Kundenportal kündigen. Rechnungen erhalten Sie per E-Mail.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
